/**
 * Number, time and money formatting per Brand Guidelines v1.1 / v1.2:
 * - Western numerals in both languages (Arabic uses the `latn` numbering system).
 * - Arabic: 24-hour time ("07:14"); English: 12-hour with AM/PM and no leading zero ("7:14 AM").
 *   Flights are always 24-hour.
 * - Every time is in the person's zone (M5.2); the zone is named once per day group when it
 *   differs from the device zone ("4:17 PM · Doha" / «16:17 · الدوحة»).
 * - Currency before the number in English ($4,300, QAR 3,000), after it in Arabic (3,000 ريال قطري).
 * - Dates as "Thursday, 12 June" / "الخميس 12 يونيو"; short dates as "24 Sep 2026" / «24 سبتمبر 2026».
 */
export function numberLocale(locale: string): string {
  const base = locale.split('-u-')[0] ?? locale;
  return base.startsWith('ar') ? `${base}-u-nu-latn` : base;
}

const isArabic = (locale: string) => locale.startsWith('ar');

/** The zone the device reports; the person's zone may differ. */
export function deviceZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function timeFormatter(
  locale: string,
  opts: { flight?: boolean; timeZone?: string | undefined } = {},
): Intl.DateTimeFormat {
  const twentyFour = isArabic(locale) || opts.flight === true;
  return new Intl.DateTimeFormat(numberLocale(locale), {
    hour: twentyFour ? '2-digit' : 'numeric',
    minute: '2-digit',
    hourCycle: twentyFour ? 'h23' : 'h12',
    ...(opts.timeZone ? { timeZone: opts.timeZone } : {}),
  });
}

/** "Thursday, 12 June" in English, "الخميس 12 يونيو" in Arabic (Western numerals). */
export function formatDate(date: Date, locale: string, timeZone?: string): string {
  const parts = new Intl.DateTimeFormat(numberLocale(locale), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    ...(timeZone ? { timeZone } : {}),
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  const weekday = get('weekday');
  const day = get('day');
  const month = get('month');
  return isArabic(locale) ? `${weekday} ${day} ${month}` : `${weekday}, ${day} ${month}`;
}

/** "24 Sep 2026" / «24 سبتمبر 2026» (M5.2, as on the site). */
export function formatShortDate(date: Date, locale: string, timeZone?: string): string {
  const parts = new Intl.DateTimeFormat(numberLocale(locale), {
    day: 'numeric',
    month: isArabic(locale) ? 'long' : 'short',
    year: 'numeric',
    ...(timeZone ? { timeZone } : {}),
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${get('day')} ${get('month')} ${get('year')}`;
}

/** The calendar day of an instant in a zone, as YYYY-MM-DD (day grouping). */
export function dayKey(iso: string, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}

const ZONE_CITIES: Record<string, { en: string; ar: string }> = {
  'Asia/Qatar': { en: 'Doha', ar: 'الدوحة' },
  'Asia/Dubai': { en: 'Dubai', ar: 'دبي' },
  'Asia/Riyadh': { en: 'Riyadh', ar: 'الرياض' },
  'Asia/Kuwait': { en: 'Kuwait', ar: 'الكويت' },
  'Asia/Bahrain': { en: 'Manama', ar: 'المنامة' },
  'Asia/Muscat': { en: 'Muscat', ar: 'مسقط' },
  'Asia/Amman': { en: 'Amman', ar: 'عمّان' },
  'Africa/Cairo': { en: 'Cairo', ar: 'القاهرة' },
  'Europe/London': { en: 'London', ar: 'لندن' },
  'Europe/Paris': { en: 'Paris', ar: 'باريس' },
  'America/New_York': { en: 'New York', ar: 'نيويورك' },
  UTC: { en: 'UTC', ar: 'UTC' },
};

/** The city a zone is named after ("Doha" / «الدوحة»); the zone's last segment otherwise. */
export function zoneLabel(timeZone: string, locale: string): string {
  const known = ZONE_CITIES[timeZone];
  if (known) return isArabic(locale) ? known.ar : known.en;
  return (timeZone.split('/').pop() ?? timeZone).replace(/_/gu, ' ');
}

/** "4:17 PM · Doha" when the person's zone differs from the device zone; the time alone otherwise. */
export function timeWithZone(
  iso: string,
  locale: string,
  timeZone: string,
  device = deviceZone(),
): string {
  const time = timeFormatter(locale, { timeZone }).format(new Date(iso));
  return timeZone === device ? time : `${time} · ${zoneLabel(timeZone, locale)}`;
}

export function formatMoney(amount: string | number, currency: string, locale: string): string {
  const n = Number(amount);
  if (!Number.isFinite(n)) return `${amount} ${currency}`;
  const fraction = n % 1 === 0 ? 0 : 2;
  try {
    return new Intl.NumberFormat(numberLocale(locale), {
      style: 'currency',
      currency,
      currencyDisplay: isArabic(locale) ? 'name' : 'symbol',
      maximumFractionDigits: fraction,
      minimumFractionDigits: fraction,
    }).format(n);
  } catch {
    return `${amount} ${currency}`;
  }
}

/** Relative "x min ago" style label with Western numerals. */
export function relativeTime(iso: string, locale: string, now = Date.now()): string {
  const diff = now - new Date(iso).getTime();
  const rtf = new Intl.RelativeTimeFormat(numberLocale(locale), { numeric: 'auto' });
  const m = Math.round(diff / 60_000);
  if (Math.abs(m) < 60) return rtf.format(-m, 'minute');
  const h = Math.round(m / 60);
  if (Math.abs(h) < 24) return rtf.format(-h, 'hour');
  return rtf.format(-Math.round(h / 24), 'day');
}

/**
 * "1 hour ago · 4:40 PM" (M5.2): every relative time carries the absolute time in the person's
 * zone; the date is added when the instant is not today in that zone.
 */
export function relativeWithAbsolute(
  iso: string,
  locale: string,
  timeZone: string,
  now = Date.now(),
): string {
  const time = timeFormatter(locale, { timeZone }).format(new Date(iso));
  const today = dayKey(new Date(now).toISOString(), timeZone) === dayKey(iso, timeZone);
  const absolute = today ? time : `${formatShortDate(new Date(iso), locale, timeZone)} ${time}`;
  return `${relativeTime(iso, locale, now)} · ${absolute}`;
}
